/**
 * GemHub & Co. — Video Available Badge  v3.1 (GH redesign; class names avoid 'badge' so luxury-emerald.css [class*=badge] reset cannot strip them)
 * --------------------------------------------------------------------
 * FIX (v2.2): YouTube/Vimeo embeds living inside <deferred-media>
 *   <template> elements are now correctly detected. Two issues fixed:
 *     (a) VIDEO_URL_RE didn't match YouTube/Vimeo URLs, so the
 *         template-HTML check returned false even when an iframe was
 *         present.
 *     (b) querySelector() on a <deferred-media> does NOT traverse into
 *         its <template>.content (DocumentFragment), so the iframe
 *         check missed iframes that hadn't been hydrated yet. We now
 *         look inside template.content explicitly.
 *
 * FIX (v2.1): badge no longer appears on products that have no video
 *   uploaded. pageHasVideo() is scoped to the product media gallery,
 *   every branch requires a real video source URL, the loose
 *   [aria-label="play"] catch-all is removed, and a cleanup pass
 *   removes badges if a re-render reveals no video.
 *
 * Detection rules (must match at least one, scoped to the gallery):
 *   - <video> with a non-empty src or a <source> child with a non-empty src
 *   - <deferred-media> whose <template> contains a <video>/<source>/<iframe>
 *     pointing at a real video URL (Shopify CDN MP4/WebM/HLS, YouTube,
 *     YouTube-nocookie, Vimeo, youtu.be)
 *   - any element whose src/data-src points at one of those video hosts
 *   - an iframe (rendered or inside a template) embedding YouTube/Vimeo
 * --------------------------------------------------------------------
 */
(function () {
  'use strict';

  /* ─── 1. CONSTANTS ─────────────────────────────────────────────────── */
  var BADGE_CLASS        = 'ghco-video-pill';
  var TITLE_BADGE_CLASS  = 'ghco-video-title-pill';
  var THUMB_LABEL_CLASS  = 'ghco-thumb-video-tag';
  var STYLES_ID          = 'ghco-video-badge-styles';
  var OBSERVER_KEY       = '__ghco_observer__';

  // URL patterns that indicate a real product video — uploaded MP4/WebM/HLS
  // OR an external embed (YouTube/Vimeo). Without the embed entries, products
  // whose video media is a YouTube/Vimeo iframe inside a <template> would be
  // missed.
  var VIDEO_URL_RE = /(?:\.mp4|\.webm|\.m3u8|cdn\.shopify\.com\/videos|shopifycdn\.com\/videos|videos\/c\/vp|youtube\.com\/embed|youtube-nocookie\.com\/embed|youtu\.be\/|player\.vimeo\.com\/video|vimeo\.com\/\d)/i;

  // Selectors that identify the product media gallery container(s).
  // We try them in order and use whichever exists.
  var GALLERY_SELECTORS = [
    'ml-media-gallery',
    '.ML_Media_Gallery',
    'ml-media-gallery-viewer',
    'product-media-gallery',
    '.product__media-list',
    '.product-single__media',
    '.product__media-wrapper'
  ];

  /* ─── 2. CSS (unchanged) ────────────────────────────────────────────── */
  function injectStyles() {
    if (document.getElementById(STYLES_ID)) return;
    var s = document.createElement('style');
    s.id = STYLES_ID;
    s.textContent = [
      /* GH v3 — refined "Watch video" pill: frosted navy glass, gold play disc, single calm line */
      '.' + BADGE_CLASS + '{',
        'position:absolute;top:16px;left:16px;z-index:50;box-sizing:border-box;',
        'display:inline-flex;align-items:center;gap:10px;margin:0;',
        'padding:6px 18px 6px 6px;border-radius:999px;',
        'background:rgba(4,27,45,.68);color:#fff;',
        'border:1px solid rgba(255,255,255,.16);',
        'box-shadow:0 8px 24px rgba(4,27,45,.28),inset 0 1px 0 rgba(255,255,255,.08);',
        '-webkit-backdrop-filter:blur(12px) saturate(1.3);backdrop-filter:blur(12px) saturate(1.3);',
        'font-family:var(--gh-font-body,"Jost",system-ui,sans-serif);font-size:11px;font-weight:600;',
        'letter-spacing:.18em;text-transform:uppercase;line-height:1;white-space:nowrap;',
        'pointer-events:auto;cursor:pointer;user-select:none;-webkit-tap-highlight-color:transparent;',
        'transition:background .25s ease,border-color .25s ease,transform .25s ease,box-shadow .25s ease;',
        'animation:ghco-fadein .6s cubic-bezier(.2,.7,.2,1) .35s both;',
      '}',
      '.' + BADGE_CLASS + ':hover{background:rgba(4,27,45,.86);border-color:rgba(201,168,76,.6);transform:translateY(-1px);box-shadow:0 12px 28px rgba(4,27,45,.34);}',
      '.' + BADGE_CLASS + ':active{transform:translateY(0);}',
      '.' + BADGE_CLASS + ' .ghco-play-icon{',
        'position:relative;display:flex;align-items:center;justify-content:center;flex-shrink:0;',
        'width:30px;height:30px;border-radius:50%;',
        'background:linear-gradient(145deg,#f3dc94 0%,#c9a84c 55%,#a8843a 100%);',
        'box-shadow:0 2px 6px rgba(0,0,0,.25),inset 0 1px 0 rgba(255,255,255,.45);',
      '}',
      '.' + BADGE_CLASS + ' .ghco-play-icon::before{',
        'content:"";position:absolute;inset:-4px;border-radius:50%;',
        'border:1.5px solid rgba(201,168,76,.9);',
        'animation:ghco-halo 2.8s cubic-bezier(.2,.6,.3,1) infinite 1s;',
      '}',
      '.' + BADGE_CLASS + ' .ghco-play-icon svg{display:block;width:10px;height:11px;margin-left:2px;}',
      '.' + BADGE_CLASS + ' .ghco-pill-text{display:flex;flex-direction:column;gap:3px;line-height:1;}',
      '.' + BADGE_CLASS + ' .ghco-pill-line1{font-size:8.5px;font-weight:500;letter-spacing:.22em;color:#e3bf76;opacity:.95;}',
      '.' + BADGE_CLASS + ' .ghco-pill-line2{font-size:11px;font-weight:600;letter-spacing:.18em;color:#fff;}',
      '.' + BADGE_CLASS + ' *{box-sizing:border-box;border:0;outline:0;background-clip:padding-box;}',
      '.' + TITLE_BADGE_CLASS + '{',
        'display:inline-flex;align-items:center;gap:6px;vertical-align:middle;',
        'margin-left:10px;position:relative;top:-3px;padding:4px 10px 4px 7px;border-radius:999px;',
        'background:rgba(201,168,76,.10);color:#0D3B3E;border:1px solid rgba(201,168,76,.55);',
        'font-family:var(--gh-font-body,"Jost",system-ui,sans-serif);font-size:9.5px;font-weight:600;',
        'letter-spacing:.18em;text-transform:uppercase;line-height:1;white-space:nowrap;',
        'animation:ghco-fadein .5s ease .4s both;',
      '}',
      '.' + TITLE_BADGE_CLASS + ' svg{flex-shrink:0;}',
      '.' + THUMB_LABEL_CLASS + '{',
        'position:absolute;bottom:6px;left:50%;transform:translateX(-50%);z-index:10;',
        'display:inline-flex;align-items:center;gap:4px;padding:3px 7px;border-radius:999px;',
        'background:rgba(4,27,45,.78);color:#fff;border:1px solid rgba(255,255,255,.14);',
        '-webkit-backdrop-filter:blur(6px);backdrop-filter:blur(6px);',
        'font-family:var(--gh-font-body,"Jost",system-ui,sans-serif);font-size:7.5px;font-weight:600;',
        'letter-spacing:.16em;text-transform:uppercase;line-height:1;white-space:nowrap;pointer-events:none;',
      '}',
      '.' + THUMB_LABEL_CLASS + '::before{content:"";width:0;height:0;border-style:solid;border-width:3px 0 3px 5px;border-color:transparent transparent transparent #e3bf76;}',
      '@keyframes ghco-halo{',
        '0%{transform:scale(.85);opacity:.9}',
        '70%{transform:scale(1.45);opacity:0}',
        '100%{transform:scale(1.45);opacity:0}',
      '}',
      '@keyframes ghco-fadein{',
        'from{opacity:0;transform:translateY(-6px)}',
        'to{opacity:1;transform:translateY(0)}',
      '}',
      '@media (prefers-reduced-motion:reduce){.' + BADGE_CLASS + ' .ghco-play-icon::before{animation:none;opacity:.7;}}',
      '@media screen and (max-width:749px){',
        '.' + BADGE_CLASS + '{top:12px;left:12px;padding:5px 14px 5px 5px;gap:8px;}',
        '.' + BADGE_CLASS + ' .ghco-play-icon{width:26px;height:26px;}',
        '.' + BADGE_CLASS + ' .ghco-pill-line2{font-size:10px;}',
      '}'
    ].join('');
    document.head.appendChild(s);
  }

  /* ─── 3. SCOPED VIDEO DETECTION ─────────────────────────────────────── */

  // Find the product media gallery root. Returns null if none on this page,
  // which means we should NOT show the badge.
  function getGalleryRoot() {
    for (var i = 0; i < GALLERY_SELECTORS.length; i++) {
      var el = document.querySelector(GALLERY_SELECTORS[i]);
      if (el) return el;
    }
    return null;
  }

  // Does this <video> element have a real, non-empty source?
  function videoHasSource(v) {
    var src = v.getAttribute('src');
    if (src && src.trim().length > 0) return true;
    var sourceChild = v.querySelector('source[src]');
    if (sourceChild) {
      var ssrc = sourceChild.getAttribute('src');
      if (ssrc && ssrc.trim().length > 0) return true;
    }
    return false;
  }

  // Does this <deferred-media> wrap a real product video?
  // Shopify themes often render <deferred-media> as a placeholder; the
  // actual <video>/<source>/<iframe> lives inside an inert <template>
  // until clicked. Note: querySelector on the <deferred-media> does NOT
  // see into template.content (DocumentFragment), so we have to check it
  // explicitly.
  function deferredMediaHasVideo(d) {
    // Already-hydrated child
    var live = d.querySelector('video');
    if (live && videoHasSource(live)) return true;

    var liveIframe = d.querySelector('iframe[src*="youtube"],iframe[src*="youtu.be"],iframe[src*="vimeo"]');
    if (liveIframe) return true;

    // Inspect the inert template payload
    var tpl = d.querySelector('template');
    if (tpl) {
      var html = tpl.innerHTML || '';
      if (VIDEO_URL_RE.test(html)) return true;
      if (/<video[^>]+\ssrc\s*=\s*["'][^"']+["']/i.test(html)) return true;
      if (/<source[^>]+\ssrc\s*=\s*["'][^"']+["']/i.test(html)) return true;
      if (/<iframe[^>]+\ssrc\s*=\s*["'][^"']+["']/i.test(html)) {
        // Iframe in template — check via DocumentFragment so we get the
        // real, parsed src (handles HTML-entity-encoded URLs etc.).
        if (tpl.content) {
          var tplVideo = tpl.content.querySelector('video');
          if (tplVideo && (tplVideo.getAttribute('src') || tplVideo.querySelector('source[src]'))) return true;
          var tplIframe = tpl.content.querySelector('iframe[src]');
          if (tplIframe) {
            var ifsrc = tplIframe.getAttribute('src') || '';
            if (VIDEO_URL_RE.test(ifsrc)) return true;
          }
        }
      }
    }

    // Some themes set data attributes describing the video source
    var dataSrc = d.getAttribute('data-video-src') || d.getAttribute('data-src') || '';
    if (dataSrc && VIDEO_URL_RE.test(dataSrc)) return true;

    return false;
  }

  // Returns true only if the gallery contains an actual uploaded/embedded video.
  function pageHasVideo() {
    var scope = getGalleryRoot();
    if (!scope) return false;

    // <video> with a real source
    var videos = scope.querySelectorAll('video');
    for (var i = 0; i < videos.length; i++) {
      if (videoHasSource(videos[i])) return true;
    }

    // <deferred-media> wrapping a real video
    var deferred = scope.querySelectorAll('deferred-media');
    for (var j = 0; j < deferred.length; j++) {
      if (deferredMediaHasVideo(deferred[j])) return true;
    }

    // <source> with a video URL
    var sources = scope.querySelectorAll('source[src],source[type*="video"]');
    for (var k = 0; k < sources.length; k++) {
      var ssrc = sources[k].getAttribute('src') || '';
      var stype = sources[k].getAttribute('type') || '';
      if (VIDEO_URL_RE.test(ssrc) || /^video\//i.test(stype)) return true;
    }

    // Anything pointing at the Shopify videos CDN
    var cdn = scope.querySelectorAll('[src],[data-src],[data-video-src]');
    for (var m = 0; m < cdn.length; m++) {
      var attrs = cdn[m].attributes;
      for (var n = 0; n < attrs.length; n++) {
        if (VIDEO_URL_RE.test(attrs[n].value)) return true;
      }
    }

    // External video iframes (YouTube/Vimeo) already rendered in the gallery
    if (scope.querySelector('iframe[src*="youtube"],iframe[src*="youtu.be"],iframe[src*="vimeo"]')) return true;

    // External video iframes inside any <template> in the gallery
    // (querySelector won't recurse into template.content, so do it manually).
    var tpls = scope.querySelectorAll('template');
    for (var p = 0; p < tpls.length; p++) {
      var c = tpls[p].content;
      if (!c) continue;
      var ifr = c.querySelector('iframe[src*="youtube"],iframe[src*="youtu.be"],iframe[src*="vimeo"]');
      if (ifr) return true;
      var v2 = c.querySelector('video[src],video source[src]');
      if (v2) return true;
    }

    return false;
  }

  // Per-thumbnail check used when labelling individual thumbnails.
  function thumbHasVideo(thumb) {
    var v = thumb.querySelector('video');
    if (v && videoHasSource(v)) return true;

    var d = thumb.querySelector('deferred-media');
    if (d && deferredMediaHasVideo(d)) return true;

    var ifr = thumb.querySelector('iframe[src*="youtube"],iframe[src*="youtu.be"],iframe[src*="vimeo"]');
    if (ifr) return true;

    var s = thumb.querySelector('source[src]');
    if (s && VIDEO_URL_RE.test(s.getAttribute('src') || '')) return true;

    // data-media-type="video" on the thumbnail itself
    var mediaType = thumb.getAttribute('data-media-type') || '';
    if (/video|external_video/i.test(mediaType) && (
          thumb.querySelector('[src],[data-src],[data-video-src]')
        )) {
      // Only trust data-media-type if there's also some src-bearing child.
      var attrEls = thumb.querySelectorAll('[src],[data-src],[data-video-src]');
      for (var i = 0; i < attrEls.length; i++) {
        var aa = attrEls[i].attributes;
        for (var j = 0; j < aa.length; j++) {
          if (VIDEO_URL_RE.test(aa[j].value)) return true;
        }
      }
    }

    return false;
  }

  /* ─── 4. BADGE RENDERING ────────────────────────────────────────────── */
  function renderBadges() {
    if (!pageHasVideo()) return false;
    injectStyles();
    var added = false;

    var viewer = document.querySelector('ml-media-gallery-viewer');
    if (viewer && !viewer.querySelector('.' + BADGE_CLASS)) {
      viewer.style.position = 'relative';
      var badge = document.createElement('div');
      badge.className = BADGE_CLASS;
      badge.setAttribute('aria-label', 'Watch the product video');
      badge.setAttribute('role', 'button');
      badge.setAttribute('tabindex', '0');
      badge.innerHTML =
        '<span class="ghco-play-icon" aria-hidden="true">' +
          '<svg viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">' +
            '<path d="M1.2 1.1v8.8a.6.6 0 0 0 .9.5l7.2-4.4a.6.6 0 0 0 0-1L2.1.6a.6.6 0 0 0-.9.5z" fill="#041b2d"/>' +
          '</svg>' +
        '</span>' +
        '<span class="ghco-pill-text">' +
          '<span class="ghco-pill-line1">Product film</span>' +
          '<span class="ghco-pill-line2">Watch video</span>' +
        '</span>';
      viewer.insertAdjacentElement('afterbegin', badge);
      badge.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); badge.click(); }
      });

      // Click handler: navigate to the video slide and start playback
      badge.addEventListener('click', function () {
        var gallery = getGalleryRoot();
        if (!gallery) return;

        // Find the slide containing a video/deferred-media
        var slides = viewer.querySelectorAll('[data-media-index]');
        for (var si = 0; si < slides.length; si++) {
          var slide = slides[si];
          var dm = slide.querySelector('deferred-media');
          var vid = slide.querySelector('video');
          if ((dm && deferredMediaHasVideo(dm)) || (vid && videoHasSource(vid))) {
            var idx = parseInt(slide.getAttribute('data-media-index'), 10);

            // Use the gallery's built-in navigation to switch to the video slide
            if (gallery.setActiveMedia) {
              gallery.setActiveMedia(null, idx);
            } else {
              // Fallback: show/hide slides manually
              slides.forEach(function (s) { s.classList.remove('show'); s.classList.add('hide'); });
              slide.classList.remove('hide');
              slide.classList.add('show');
            }

            // Trigger deferred-media to load and play the video
            setTimeout(function () {
              var poster = slide.querySelector('.deferred-media__poster');
              if (poster) {
                poster.click();
              } else if (dm && dm.loadContent) {
                dm.loadContent();
              }
            }, 150);
            break;
          }
        }
      });

      added = true;
    }

    var titleEl = document.querySelector('h1.ml_product_title');
    if (titleEl && !titleEl.querySelector('.' + TITLE_BADGE_CLASS)) {
      var tb = document.createElement('span');
      tb.className = TITLE_BADGE_CLASS;
      tb.innerHTML =
        '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">' +
          '<circle cx="5" cy="5" r="4.5" stroke="#c9a84c" stroke-width="1"/>' +
          '<path d="M3.5 3.2L7 5L3.5 6.8V3.2Z" fill="#c9a84c"/>' +
        '</svg>Video';
      titleEl.appendChild(tb);
      added = true;
    }

    var thumbHolder = document.querySelector('.ML_Media_Gallery_Thumbnail_Holder');
    if (thumbHolder) {
      Array.from(thumbHolder.children).forEach(function (thumb) {
        if (thumbHasVideo(thumb) && !thumb.querySelector('.' + THUMB_LABEL_CLASS)) {
          thumb.style.position = 'relative';
          var lbl = document.createElement('span');
          lbl.className = THUMB_LABEL_CLASS;
          lbl.textContent = 'VIDEO';
          thumb.appendChild(lbl);
          added = true;
        }
      });
    }

    return added;
  }

  // Cleanup helper — removes any badges that were rendered earlier on a
  // page that we now believe has no video. Useful when the gallery
  // re-renders (variant switch / section reload).
  function removeBadgesIfNoVideo() {
    if (pageHasVideo()) return;
    document.querySelectorAll('.' + BADGE_CLASS + ',.' + TITLE_BADGE_CLASS + ',.' + THUMB_LABEL_CLASS)
      .forEach(function (n) { n.parentNode && n.parentNode.removeChild(n); });
  }

  /* ─── 5. SMART RETRY LOOP ───────────────────────────────────────────── */
  var _retryCount = 0;
  var _maxRetries = 12;
  var _retryDone  = false;

  function tryRender() {
    if (_retryDone) return;
    if (renderBadges()) { _retryDone = true; return; }
    _retryCount++;
    if (_retryCount < _maxRetries) setTimeout(tryRender, 500);
  }

  /* ─── 6. MUTATION OBSERVER ──────────────────────────────────────────── */
  function startObserver() {
    if (window[OBSERVER_KEY]) return;
    var observer = new MutationObserver(function (mutations) {
      var relevant = false;
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        for (var j = 0; j < m.addedNodes.length; j++) {
          var node = m.addedNodes[j];
          if (node.nodeType !== 1) continue;
          var tag = node.tagName ? node.tagName.toLowerCase() : '';
          if (tag === 'deferred-media' || tag === 'video' || tag === 'source' ||
              tag === 'ml-media-gallery' || tag === 'ml-media-gallery-viewer' ||
              tag === 'ml-media-gallery-thumbnail' ||
              (node.querySelector && (
                node.querySelector('deferred-media') ||
                node.querySelector('video') ||
                node.querySelector('ml-media-gallery-viewer')
              ))) {
            relevant = true;
            break;
          }
        }
        if (relevant) break;
        if (m.type === 'attributes' && m.attributeName === 'src') {
          var target = m.target;
          if (target.tagName === 'SOURCE' || target.tagName === 'VIDEO') {
            relevant = true;
            break;
          }
        }
      }
      if (relevant) {
        _retryDone = false;
        _retryCount = 0;
        // Re-evaluate: render if a video appeared, remove if it went away.
        setTimeout(function () {
          tryRender();
          removeBadgesIfNoVideo();
        }, 50);
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'data-src']
    });
    window[OBSERVER_KEY] = observer;
  }

  /* ─── 7. SHOPIFY SECTION EVENTS ─────────────────────────────────────── */
  function listenShopifyEvents() {
    document.addEventListener('shopify:section:load', function () {
      _retryDone = false; _retryCount = 0;
      setTimeout(function () { tryRender(); removeBadgesIfNoVideo(); }, 100);
    });
    document.addEventListener('shopify:section:select', function () {
      _retryDone = false; _retryCount = 0;
      setTimeout(function () { tryRender(); removeBadgesIfNoVideo(); }, 100);
    });
  }

  /* ─── 8. CUSTOM ELEMENT READY HOOKS ────────────────────────────────── */
  function waitForCustomElements() {
    ['ml-media-gallery-viewer', 'ml-media-gallery', 'deferred-media'].forEach(function (name) {
      customElements.whenDefined(name).then(function () {
        setTimeout(tryRender, 100);
      }).catch(function () {});
    });
  }

  /* ─── 9. BOOTSTRAP ──────────────────────────────────────────────────── */
  function init() {
    if (!document.body.classList.contains('template-product') &&
        window.location.pathname.indexOf('/products/') === -1) return;
    listenShopifyEvents();
    waitForCustomElements();
    startObserver();
    tryRender();
    [300, 800, 1500, 3000, 5000].forEach(function (ms) {
      setTimeout(function () {
        if (!_retryDone) { _retryCount = 0; tryRender(); }
        // After the page has fully settled, do a final cleanup pass so any
        // badges added by an earlier (looser) detection get removed if the
        // gallery turns out to have no real video.
        if (ms === 5000) removeBadgesIfNoVideo();
      }, ms);
    });
  }

  /* ─── 10. ENTRY POINTS ──────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  window.addEventListener('load', function () {
    if (!_retryDone) { _retryCount = 0; tryRender(); }
    setTimeout(removeBadgesIfNoVideo, 1500);
  });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible' && !_retryDone) {
      _retryCount = 0; tryRender();
    }
  });

})();